// /Users/joshua.moore1153/Desktop/Sinclair Apps/spcore-v4/src/components/CcpAdminAnnouncements/inc/AddForm.js
import React, { useState } from "react";
import {
  TextField,
  Button,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  Alert,
} from "@mui/material";
import { AddCircle as AddIcon } from "@mui/icons-material";
import { Timestamp } from "firebase/firestore"; // Import Timestamp
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const AddForm = ({ addData }) => {
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("");
  const [expireDate, setExpireDate] = useState(""); // Input value is YYYY-MM-DD string
  const [content, setContent] = useState("");
  const [url, setUrl] = useState(""); // Add URL state
  const [linkText, setLinkText] = useState(""); // Add link text state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogOpen(false);
      // Reset form on cancel if desired
      // resetForm();
    }
  };

  const resetForm = () => {
    setTitle("");
    setColor("");
    setExpireDate("");
    setContent("");
    setUrl(""); // Reset URL field
    setLinkText(""); // Reset link text field
    setFormError(null);
    setIsSubmitting(false); // Ensure submitting state is reset
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    // --- URL Validation ---
    if (url && url.trim()) {
      try {
        new URL(url.trim());
      } catch (urlError) {
        setFormError("Please enter a valid URL (e.g., https://example.com)");
        setIsSubmitting(false);
        return;
      }
    }

    // --- Timestamp Conversion ---
    let expireTimestamp = null;
    if (expireDate) {
      try {
        // The input type="date" provides "YYYY-MM-DD"
        // We need to parse it correctly, considering potential timezone issues.
        // Creating a date at UTC midnight is often safest.
        const dateParts = expireDate.split("-"); // [YYYY, MM, DD]
        const utcDate = new Date(
          Date.UTC(
            parseInt(dateParts[0], 10),
            parseInt(dateParts[1], 10) - 1, // Month is 0-indexed
            parseInt(dateParts[2], 10)
          )
        );
        if (!isNaN(utcDate.getTime())) {
          expireTimestamp = Timestamp.fromDate(utcDate);
        } else {
          throw new Error("Invalid date format provided.");
        }
      } catch (dateError) {
        console.error("Error parsing date:", dateError);
        setFormError("Invalid Expire Date format. Please use YYYY-MM-DD.");
        setIsSubmitting(false);
        return; // Stop submission
      }
    } else {
      // Handle case where expireDate is required but empty
      setFormError("Expire Date is required.");
      setIsSubmitting(false);
      return; // Stop submission
    }
    // --- End Timestamp Conversion ---

    try {
      const newData = {
        title: title,
        color: color,
        expireDate: expireTimestamp, // Use the converted Timestamp
        content: content,
        url: url.trim(), // Add URL to the data object (trimmed)
        linkText: linkText.trim(), // Add link text to the data object (trimmed)
        // createdAt: Timestamp.now() // Add creation timestamp if needed
      };
      await addData(newData);
      setSnackbarMessage("Announcement successfully added.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      resetForm();
      handleCloseDialog();
    } catch (error) {
      console.error("Error adding item:", error);
      const errorMessage =
        error.message || "Failed to add announcement. Please try again.";
      setFormError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsSubmitting(false); // Re-enable on error
    }
    // Removed finally block as resetForm/handleCloseDialog only happen on success now
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        color="success"
        startIcon={<AddIcon />}
        onClick={handleOpenDialog}
        sx={{ mb: 2 }}
      >
        Add Announcement
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isSubmitting}
      >
        <ModalDialogTitle onClose={handleCloseDialog}>
          Add New Announcement
        </ModalDialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              margin="dense"
              disabled={isSubmitting}
            />
            <FormControl
              fullWidth
              required
              margin="dense"
              disabled={isSubmitting}
            >
              <InputLabel id="color-select-label">Color</InputLabel>
              <Select
                labelId="color-select-label"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                label="Color"
              >
                <MenuItem value="" disabled>
                  <em>Select a color</em>
                </MenuItem>
                <MenuItem value="info">Blue</MenuItem>
                <MenuItem value="success">Green</MenuItem>
                <MenuItem value="warning">Orange</MenuItem>
                <MenuItem value="error">Red</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Expire Date"
              value={expireDate} // Value is YYYY-MM-DD string
              onChange={(e) => setExpireDate(e.target.value)}
              fullWidth
              required
              margin="dense"
              type="date" // This input expects/provides YYYY-MM-DD
              InputLabelProps={{
                shrink: true,
              }}
              disabled={isSubmitting}
            />
            <TextField
              label="Content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              required
              margin="dense"
              multiline
              rows={4}
              disabled={isSubmitting}
            />
            <TextField
              label="URL (optional)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              margin="dense"
              placeholder="https://example.com"
              disabled={isSubmitting}
              helperText="Optional: Add a link that users can click to learn more"
            />
            <TextField
              label="Link Text (optional)"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              fullWidth
              margin="dense"
              placeholder="Learn More"
              disabled={isSubmitting}
              helperText="Custom text for the link button (only used if URL is provided)"
            />
          </DialogContent>
        </form>
        <ModalActions
          onSave={handleFormSubmit}
          saving={isSubmitting}
          saveLabel="Create Announcement"
        />
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddForm;
